import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Layout from '../../../components/shared/Layout/Layout';
import Button from '../../../components/shared/Button/Button';
import Loading from '../../../components/shared/Loading/Loading';
import ShareButtons from '../../../components/shared/ShareButtons/ShareButtons';
import { activityShareUrl, activityShareText } from '../../../utils/share';
import { saveRef } from '../../../utils/promoterRef';
import {
  DetailContainer,
  ImageSection,
  MainImage,
  ImageGallery,
  ThumbnailImage,
  ContentSection,
  TitleSection,
  Title,
  Location,
  Rating,
  Price,
  Duration,
  Description,
  Features,
  FeatureItem,
  BookingSection,
  BookingCard,
  DateSelector,
  TimeSelector,
  QuantitySelector,
  TotalPrice,
  Reviews,
  ReviewItem,
  ReviewHeader,
  ReviewText,
} from './ActivityDetail.styles';

const FALLBACK_IMAGE = 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=900&h=560&fit=crop';

const ActivityDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { search } = useLocation();
  const [activity, setActivity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [mainImage, setMainImage] = useState('');
  const [bookingError, setBookingError] = useState('');

  // Enlace de un promotor: /activity/<id>?ref=CODIGO. El código se guarda solo
  // en sessionStorage (ligado a esta actividad) y se manda al reservar.
  useEffect(() => {
    const code = new URLSearchParams(search).get('ref');
    if (code) saveRef(id, code);
  }, [id, search]);

  useEffect(() => {
    fetchActivity();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchActivity = async () => {
    try {
      const response = await fetch(`/api/activities/${id}`);
      const data = await response.json();
      if (data.success) {
        setActivity(data.activity);
        setMainImage(data.activity.images?.[0] || FALLBACK_IMAGE);
      } else {
        setActivity(null);
      }
    } catch (error) {
      console.error('Error fetching activity:', error);
      setActivity(null);
    } finally {
      setLoading(false);
    }
  };

  const checkAvailability = async () => {
    try {
      const response = await fetch(
        `/api/activities/${id}/availability?date=${selectedDate}&time=${selectedTime}&quantity=${quantity}`
      );
      const data = await response.json();

      if (!data.success || !data.available) {
        setBookingError(
          data.remainingCapacity > 0
            ? `Solo quedan ${data.remainingCapacity} lugares para esa fecha`
            : data.message || 'No hay disponibilidad para esa fecha'
        );
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error checking availability:', error);
      return true;
    }
  };

  const handleBooking = async () => {
    setBookingError('');

    if (!selectedDate || !selectedTime) {
      setBookingError('Selecciona fecha y hora para continuar');
      return;
    }

    const isAvailable = await checkAvailability();
    if (!isAvailable) return;

    navigate(`/checkout/${id}`, {
      state: {
        activityId: id,
        activityTitle: activity.title,
        activityLocation: activity.location,
        date: selectedDate,
        time: selectedTime,
        quantity,
        totalPrice: activity.price * quantity,
        // La actividad pide CURP por asistente: el checkout lo muestra desde el principio
        requireCurp: Boolean(activity.requireCurp),
      },
    });
  };

  const getTomorrowDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  };

  if (loading) {
    return (
      <Layout>
        <Loading text="Cargando actividad..." />
      </Layout>
    );
  }

  if (!activity) {
    return (
      <Layout>
        <div style={{ textAlign: 'center', padding: '64px 20px' }}>
          <h1>Actividad no encontrada</h1>
          <p style={{ color: 'var(--text-secondary)', margin: '12px 0 24px' }}>
            Puede que ya no esté disponible.
          </p>
          <Button onClick={() => navigate('/search')}>Volver a buscar</Button>
        </div>
      </Layout>
    );
  }

  const hasTimeSlots = activity.timeSlots?.length > 0;

  return (
    <Layout>
      <DetailContainer>
        <ImageSection>
          <MainImage src={mainImage} alt={activity.title} />
          {activity.images?.length > 1 && (
            <ImageGallery>
              {activity.images.map((image, index) => (
                <ThumbnailImage
                  key={index}
                  src={image}
                  alt={`${activity.title} ${index + 1}`}
                  $active={image === mainImage}
                  onClick={() => setMainImage(image)}
                />
              ))}
            </ImageGallery>
          )}
        </ImageSection>

        <ContentSection>
          <TitleSection>
            {activity.type && <span className="type-chip">{activity.type}</span>}
            <Title>{activity.title}</Title>
            <Location>📍 {activity.location}</Location>
            {activity.reviewCount > 0 && (
              <Rating>⭐ {activity.rating} ({activity.reviewCount} reseñas)</Rating>
            )}
          </TitleSection>

          <div style={{ margin: '4px 0 20px' }}>
            <ShareButtons
              title={activity.title}
              text={activityShareText(activity)}
              url={activityShareUrl(activity.id)}
            />
          </div>

          <div className="price-row">
            <Price>
              ${activity.price?.toLocaleString()}
              <span> / persona</span>
            </Price>
            {activity.duration && <Duration>🕐 {activity.duration}</Duration>}
          </div>

          <Description>{activity.longDescription || activity.description}</Description>

          {activity.features?.length > 0 && (
            <Features>
              <h3>Lo que incluye</h3>
              <div className="features-grid">
                {activity.features.map((feature, index) => (
                  <FeatureItem key={index}>{feature}</FeatureItem>
                ))}
              </div>
            </Features>
          )}
        </ContentSection>

        <BookingSection>
          <BookingCard>
            <h3>Reserva esta experiencia</h3>

            <DateSelector>
              <label>Fecha</label>
              <input
                type="date"
                min={getTomorrowDate()}
                value={selectedDate}
                onChange={(e) => { setSelectedDate(e.target.value); setBookingError(''); }}
              />
            </DateSelector>

            <TimeSelector>
              <label>Hora</label>
              <select
                value={selectedTime}
                onChange={(e) => { setSelectedTime(e.target.value); setBookingError(''); }}
              >
                <option value="">Selecciona una hora</option>
                {hasTimeSlots ? (
                  activity.timeSlots.map(slot => (
                    <option key={slot.time} value={slot.time}>
                      {slot.time}{slot.specialPrice ? ` — $${slot.specialPrice}` : ''}
                    </option>
                  ))
                ) : (
                  <option value="10:00">10:00</option>
                )}
              </select>
            </TimeSelector>

            <QuantitySelector>
              <label>Personas</label>
              <div className="stepper">
                <button
                  type="button"
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  disabled={quantity <= 1}
                  aria-label="Menos personas"
                >
                  −
                </button>
                <span>{quantity}</span>
                <button
                  type="button"
                  onClick={() => setQuantity(quantity + 1)}
                  aria-label="Más personas"
                >
                  +
                </button>
              </div>
            </QuantitySelector>

            <TotalPrice>
              <span>Total</span>
              <span>${(activity.price * quantity)?.toLocaleString()}</span>
            </TotalPrice>

            {bookingError && <p className="booking-error">{bookingError}</p>}

            <Button fullWidth onClick={handleBooking}>
              Reservar ahora
            </Button>
            <p className="hold-note">
              🔒 El pago se retiene hasta que uses tu QR en el lugar
            </p>
          </BookingCard>
        </BookingSection>

        <Reviews>
          <h3>Reseñas de viajeros</h3>
          {activity.reviews?.length > 0 ? (
            activity.reviews.map(review => (
              <ReviewItem key={review.id}>
                <ReviewHeader>
                  <div>
                    <strong>{review.user}</strong>
                    <span>{'⭐'.repeat(review.rating)}</span>
                  </div>
                  <span>{new Date(review.date).toLocaleDateString()}</span>
                </ReviewHeader>
                <ReviewText>{review.text}</ReviewText>
              </ReviewItem>
            ))
          ) : (
            <p className="no-reviews">
              Aún no hay reseñas para esta actividad. ¡Sé el primero en vivirla!
            </p>
          )}
        </Reviews>
      </DetailContainer>
    </Layout>
  );
};

export default ActivityDetail;
